// frontend/src/services/ecommerceApi.js - ACTUALIZADO CON ENDPOINTS COMPLETOS
import apiClient from './api';
import i18n from '../i18n/config';

export const ecommerceApi = {
    // Product Categories
    categories: {
        getAll: (params = {}) => {
            const searchParams = new URLSearchParams();
            if (params.parent_id) searchParams.set('parent_id', params.parent_id);
            if (params.is_active !== undefined) searchParams.set('is_active', params.is_active);

            const queryString = searchParams.toString();
            return apiClient.get(`/products/categories${queryString ? `?${queryString}` : ''}`);
        },

        getById: (id) => apiClient.get(`/products/categories/${id}`)
    },

    // Products
    products: {
        getAll: (params = {}) => {
            const normalizedParams = { ...params };
            if (normalizedParams.per_page && Number(normalizedParams.per_page) > 100) {
                normalizedParams.per_page = 100;
            }

            const searchParams = new URLSearchParams();

            Object.entries(normalizedParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    searchParams.set(key, value);
                }
            });

            const queryString = searchParams.toString();
            return apiClient.get(`/products/${queryString ? `?${queryString}` : ''}`);
        },

        getById: (id) => apiClient.get(`/products/${id}`),

        getFeatured: (limit = 8) => apiClient.get(`/products/featured/?limit=${limit}`),

        checkAvailability: (productId, quantity = 1) =>
            apiClient.get(`/products/${productId}/availability?quantity=${quantity}`),

        search: (query, limit = 5) =>
            apiClient.get(`/products/search/suggestions?q=${encodeURIComponent(query)}&limit=${limit}`)
    },

    // Product Kits
    kits: {
        getAll: (params = {}) => {
            const normalizedParams = { ...params };
            if (normalizedParams.per_page && Number(normalizedParams.per_page) > 100) {
                normalizedParams.per_page = 100;
            }

            const searchParams = new URLSearchParams();

            Object.entries(normalizedParams).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    searchParams.set(key, value);
                }
            });

            const queryString = searchParams.toString();
            return apiClient.get(`/products/kits/${queryString ? `?${queryString}` : ''}`);
        },

        getById: (id) => apiClient.get(`/products/kits/${id}`),

        getProducts: (kitId) => apiClient.get(`/products/kits/${kitId}/products`),

        getFeatured: (limit = 4) => apiClient.get(`/products/featured/kits?limit=${limit}`)
    },

    // Shopping Cart
    cart: {
        get: () => apiClient.get('/cart/'),

        getSummary: () => apiClient.get('/cart/summary'),

        add: (item) => apiClient.post('/cart/add', item),

        updateItem: (itemId, quantity) =>
            apiClient.put(`/cart/items/${itemId}`, { quantity }),

        removeItem: (itemId) => apiClient.delete(`/cart/items/${itemId}`),

        applyCoupon: (couponCode) =>
            apiClient.post('/cart/coupon', { coupon_code: couponCode }),

        removeCoupon: () => apiClient.delete('/cart/coupon'),

        clear: () => apiClient.delete('/cart/clear'),

        validate: () => apiClient.get('/cart/validate')
    },

    // Orders
    orders: {
        create: (orderData) => apiClient.post('/orders/', orderData),

        createCheckoutSession: (payload) => apiClient.post('/orders/checkout-session', payload),

        getCheckoutSession: (sessionId) => apiClient.get(`/orders/checkout-session/${sessionId}`),

        createCheckoutPaymentIntent: (sessionId) =>
            apiClient.post(`/orders/checkout-session/${sessionId}/payment-intent`),

        finalizeCheckoutSession: (sessionId, paymentIntentId) =>
            apiClient.post(`/orders/checkout-session/${sessionId}/finalize?payment_intent_id=${encodeURIComponent(paymentIntentId)}`),

        getAll: (params = {}) => {
            const searchParams = new URLSearchParams();
            if (params.limit) searchParams.set('limit', params.limit);
            if (params.offset) searchParams.set('offset', params.offset);

            const queryString = searchParams.toString();
            return apiClient.get(`/orders/${queryString ? `?${queryString}` : ''}`);
        },

        getById: (orderId) => apiClient.get(`/orders/${orderId}`),

        cancel: (orderId) => apiClient.post(`/orders/${orderId}/cancel`),

        track: (orderId) => apiClient.get(`/orders/${orderId}/track`),

        getInvoice: (orderId) => apiClient.get(`/orders/${orderId}/invoice`),

        createPaymentIntent: (orderId) =>
            apiClient.post('/orders/payment-intent', { order_id: orderId }),
        
        getPaymentStatus: (orderId) => 
            apiClient.get(`/orders/${orderId}/payment-status`),

        addresses: {
            getAll: (addressType = null) =>
                apiClient.get(`/orders/addresses${addressType ? `?address_type=${addressType}` : ''}`),
            create: (payload) => apiClient.post('/orders/addresses', payload),
            remove: (addressId) => apiClient.delete(`/orders/addresses/${addressId}`),
        },

        paymentMethods: {
            getAll: () => apiClient.get('/orders/payment-methods'),
            create: (payload) => apiClient.post('/orders/payment-methods', payload),
            remove: (id) => apiClient.delete(`/orders/payment-methods/${id}`),
        },
    },

    // Admin endpoints (Arsenal.mx only)
    admin: {
        // Categories Management
        categories: {
            getAll: (includeInactive = false) =>
                apiClient.get(`/admin/categories?include_inactive=${includeInactive}`),

            create: (categoryData) => apiClient.post('/admin/categories', categoryData),

            update: (categoryId, categoryData) =>
                apiClient.put(`/admin/categories/${categoryId}`, categoryData),

            delete: (categoryId) => apiClient.delete(`/admin/categories/${categoryId}`)
        },

        // Products Management
        products: {
            getAll: (params = {}) => {
                const searchParams = new URLSearchParams();

                Object.entries(params).forEach(([key, value]) => {
                    if (value !== null && value !== undefined && value !== '') {
                        searchParams.set(key, value);
                    }
                });

                const queryString = searchParams.toString();
                return apiClient.get(`/admin/products${queryString ? `?${queryString}` : ''}`);
            },

            create: (productData) => apiClient.post('/admin/products', productData),

            update: (productId, productData) =>
                apiClient.put(`/admin/products/${productId}`, productData),

            delete: (productId) => apiClient.delete(`/admin/products/${productId}`)
        },

        // Kits Management
        kits: {
            getAll: (includeInactive = false) =>
                apiClient.get(`/admin/kits?include_inactive=${includeInactive}`),

            create: (kitData) => apiClient.post('/admin/kits', kitData),

            update: (kitId, kitData) => apiClient.put(`/admin/kits/${kitId}`, kitData),

            delete: (kitId) => apiClient.delete(`/admin/kits/${kitId}`)
        },

        // Orders Management
        orders: {
            getAll: (params = {}) => {
                const searchParams = new URLSearchParams();

                Object.entries(params).forEach(([key, value]) => {
                    if (value !== null && value !== undefined && value !== '') {
                        searchParams.set(key, value);
                    }
                });

                const queryString = searchParams.toString();
                return apiClient.get(`/admin/orders${queryString ? `?${queryString}` : ''}`);
            },

            updateStatus: (orderId, status, adminNotes = null) =>
                apiClient.put(`/admin/orders/${orderId}/status`, {
                    status,
                    admin_notes: adminNotes
                }),

            addShippingUpdate: (orderId, updateData) =>
                apiClient.post(`/admin/orders/${orderId}/shipping`, updateData)
        },

        // Coupons Management
        coupons: {
            getAll: (isActive = null) => {
                const params = isActive !== null ? `?is_active=${isActive}` : '';
                return apiClient.get(`/admin/coupons${params}`);
            },

            create: (couponData) => apiClient.post('/admin/coupons', couponData),

            update: (couponId, couponData) =>
                apiClient.put(`/admin/coupons/${couponId}`, couponData),

            delete: (couponId) => apiClient.delete(`/admin/coupons/${couponId}`)
        },

        // Analytics
        dashboard: () => apiClient.get('/admin/analytics/dashboard')
    }
};

// Cart helper functions (sin cambios)
// Cart helper functions
export const cartHelpers = {
    computeCartTotals: (cart) => {
        if (!cart || !cart.items) return null;

        const toNumber = (value) => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : 0;
        };
        const round2 = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;

        const items = Array.isArray(cart.items) ? cart.items : [];
        const itemCountFromItems = items.reduce((acc, item) => acc + Math.max(0, toNumber(item.quantity)), 0);
        const itemSubtotal = round2(items.reduce((acc, item) => {
            const totalPrice = toNumber(item.total_price);
            if (totalPrice > 0) return acc + totalPrice;
            return acc + (toNumber(item.unit_price) * Math.max(0, toNumber(item.quantity)));
        }, 0));

        const backendSubtotal = round2(cart.subtotal);
        const backendTax = round2(cart.tax_amount);
        const backendShipping = round2(cart.shipping_amount);
        const discountAmount = round2(cart.discount_amount);
        const backendTotal = round2(cart.total);

        const hasSubtotalMismatch = itemSubtotal > 0 && Math.abs(backendSubtotal - itemSubtotal) > 0.01;
        const subtotal = hasSubtotalMismatch ? itemSubtotal : backendSubtotal;

        const expectedTax = round2(subtotal * 0.16);
        const taxAmount = (subtotal > 0 && Math.abs(backendTax - expectedTax) > 0.01) ? expectedTax : backendTax;

        const expectedShipping = round2(subtotal >= 100 ? 0 : (items.length > 0 ? 10 : 0));
        const shippingAmount = (items.length > 0 && Math.abs(backendShipping - expectedShipping) > 0.01)
            ? expectedShipping
            : backendShipping;

        // Fuente de verdad única: total derivado SIEMPRE de los componentes del resumen.
        const computedTotal = round2(Math.max(0, subtotal - discountAmount + taxAmount + shippingAmount));
        const total = computedTotal;

        return {
            itemCount: (cart.item_count || 0) > 0 ? cart.item_count : itemCountFromItems,
            subtotal,
            taxAmount,
            shippingAmount,
            discountAmount,
            total,
            computedTotal,
            backendTotal,
            totalMismatch: Math.abs(backendTotal - computedTotal) > 0.01,
            hasDiscount: discountAmount > 0,
            hasFreeShipping: shippingAmount === 0,
            couponCode: cart.coupon_code,
            couponState: cart.coupon_state || null
        };
    },

    // Compatibilidad hacia atras: mantener API anterior apuntando al calculo centralizado.
    calculateCartTotals: (cart) => cartHelpers.computeCartTotals(cart),

    // ✅ CAMBIO: Formatear como USD
    formatCurrency: (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    },

    // ✅ CAMBIO: Umbral de envío gratis en USD
    checkFreeShipping: (subtotal, threshold = 100) => {
        return subtotal >= threshold;
    },

    calculateSavings: (cart) => {
        let totalSavings = 0;

        if (cart && cart.items) {
            cart.items.forEach(item => {
                const product = item.product || item.kit;
                if (product && product.compare_price && product.compare_price > item.unit_price) {
                    totalSavings += (product.compare_price - item.unit_price) * item.quantity;
                }
            });
        }

        if (cart && cart.discount_amount) {
            totalSavings += parseFloat(cart.discount_amount);
        }

        return totalSavings;
    }
};
// Product helper functions (sin cambios)
export const productHelpers = {
    isOnSale: (product) => {
        return product.compare_price && product.compare_price > product.price;
    },

    getDiscountPercentage: (product) => {
        return product.discount_percentage || 0;
    },

    getMainImage: (product) => {
        return product.main_image_url || product.main_image || '/images/placeholder-product.jpg';
    },

    getStockStatus: (product) => {
        if (!product.track_inventory) {
            return { status: 'in_stock', message: i18n.t('hardcoded.services_ecommerceapi.in_stock') };
        }

        if (product.stock_quantity > 10) {
            return { status: 'in_stock', message: i18n.t('hardcoded.services_ecommerceapi.in_stock') };
        } else if (product.stock_quantity > 0) {
            return { status: 'low_stock', message: i18n.t('hardcoded.services_ecommerceapi.low_stock', { count: product.stock_quantity }) };
        } else if (product.allow_backorder) {
            return { status: 'backorder', message: i18n.t('hardcoded.services_ecommerceapi.backorder') };
        } else {
            return { status: 'out_of_stock', message: i18n.t('hardcoded.services_ecommerceapi.out_of_stock') };
        }
    },

    formatSpecifications: (specs) => {
        if (!specs || typeof specs !== 'object') return [];

        return Object.entries(specs).map(([key, value]) => ({
            label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            value: value
        }));
    }
};

// Order helper functions (sin cambios)
export const orderHelpers = {
    getStatusColor: (status) => {
        const colors = {
            pending: 'warning',
            confirmed: 'info',
            processing: 'primary',
            shipped: 'success',
            delivered: 'success',
            cancelled: 'error',
            refunded: 'error'
        };
        return colors[status] || 'default';
    },

    getStatusDisplay: (status) => {
        const displays = {
            pending: 'Pendiente',
            confirmed: 'Confirmada',
            processing: 'Procesando',
            shipped: 'Enviada',
            delivered: 'Entregada',
            cancelled: 'Cancelada',
            refunded: 'Reembolsada'
        };
        return displays[status] || status;
    },

    formatTrackingUpdates: (updates) => {
        if (!updates || !Array.isArray(updates)) return [];

        return updates.map(update => ({
            ...update,
            formattedDate: new Date(update.created_at).toLocaleDateString('es-MX', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }));
    }
};

export default ecommerceApi;